/**
 * CR-202: the composer's voice dictation controls. All dictation copy lives
 * here (wuchale); the state comes from `useVoiceDictation`.
 *
 * - `VoiceDictationButton`: mic while idle, Stop while listening, a spinner
 *   while stopping (start() is a no-op then, so the button is disabled).
 * - `VoiceDictationLanguagePicker`: the recognition language (CR-202 S4),
 *   disabled while dictating so a running recognizer never changes language.
 * - `VoiceDictationStrip`: "Listening..." or the last error, above the
 *   textarea. Its live region stays mounted so screen readers announce it.
 */
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Languages, Mic, Square, X } from "lucide-react";
import type { VoiceDictationError, VoiceDictationStatus } from "../hooks/use-voice-dictation";
import {
  DICTATION_LANGUAGE_NAMES,
  type DictationLanguage,
  FIXED_DICTATION_LANGUAGES,
  parseDictationLanguage,
} from "../voice-dictation";

interface VoiceDictationButtonProps {
  status: VoiceDictationStatus;
  disabled: boolean;
  onStart: () => void;
  onStop: () => void;
}

export function VoiceDictationButton({
  status,
  disabled,
  onStart,
  onStop,
}: VoiceDictationButtonProps) {
  if (status === "idle") {
    return (
      <Button
        variant="ghost"
        size="icon"
        type="button"
        className="bg-muted text-muted-foreground shrink-0 rounded-full"
        data-testid="voice-start"
        aria-label="Start dictation"
        disabled={disabled}
        onClick={onStart}
      >
        <Mic className="size-5" />
      </Button>
    );
  }
  const stopping = status === "stopping";
  return (
    <Button
      variant="ghost"
      size="icon"
      type="button"
      className={cn(
        "bg-destructive/10 text-destructive shrink-0 rounded-full",
        !stopping && "ring-destructive/40 animate-pulse ring-2",
      )}
      data-testid="voice-stop"
      aria-label="Stop dictation"
      disabled={stopping}
      onClick={onStop}
    >
      {stopping ? (
        <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <Square className="size-3.5 fill-current" />
      )}
    </Button>
  );
}

interface VoiceDictationLanguagePickerProps {
  value: DictationLanguage;
  disabled: boolean;
  onChange: (next: DictationLanguage) => void;
}

export function VoiceDictationLanguagePicker({
  value,
  disabled,
  onChange,
}: VoiceDictationLanguagePickerProps) {
  const current = value === "browser" ? null : DICTATION_LANGUAGE_NAMES[value];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          className="text-muted-foreground h-9 shrink-0 gap-1 rounded-full px-2 text-xs"
          data-testid="voice-language"
          // Also on the Button: Radix forwards it with asChild, the tests' mock does not.
          disabled={disabled}
          data-voice-language={value}
          aria-label={
            current ? `Dictation language: ${current}` : "Dictation language: browser default"
          }
          title="Dictation language"
        >
          <Languages className="size-4" aria-hidden="true" />
          {current ? (
            <span className="hidden sm:inline" aria-hidden="true">
              {value.toUpperCase()}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Dictation language</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          aria-label="Dictation language"
          value={value}
          onValueChange={(next) => {
            const choice = parseDictationLanguage(next);
            if (choice) {
              onChange(choice);
            }
          }}
        >
          {FIXED_DICTATION_LANGUAGES.map((code) => (
            <DropdownMenuRadioItem
              key={code}
              value={code}
              data-testid="voice-language-option"
              data-value={code}
            >
              {DICTATION_LANGUAGE_NAMES[code]}
            </DropdownMenuRadioItem>
          ))}
          <DropdownMenuRadioItem
            value="browser"
            data-testid="voice-language-option"
            data-value="browser"
          >
            Browser default
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface VoiceDictationStripProps {
  status: VoiceDictationStatus;
  error: VoiceDictationError | null;
  onDismissError: () => void;
}

function ErrorMessage({ error }: { error: VoiceDictationError }) {
  if (error === "permission") {
    return (
      <>
        Microphone access is needed for dictation. Allow the microphone for this site in your
        browser settings, then try again.
      </>
    );
  }
  if (error === "no_microphone") {
    return <>No microphone was found.</>;
  }
  if (error === "no_speech") {
    return <>No speech was detected.</>;
  }
  if (error === "language") {
    return (
      <>Dictation is not available in this language. Pick another one next to the microphone.</>
    );
  }
  return <>Speech recognition is unavailable right now. You can type your message or try again.</>;
}

export function VoiceDictationStrip({ status, error, onDismissError }: VoiceDictationStripProps) {
  const listening = status === "listening";
  return (
    <div
      role="status"
      data-testid="voice-status"
      // Empty, it must not add a gap to the composer column.
      className={cn(!listening && !error && "sr-only")}
    >
      {listening ? (
        <span
          data-testid="voice-listening"
          className="text-muted-foreground inline-flex items-center gap-1.5 text-xs"
        >
          <span className="bg-destructive size-2 animate-pulse rounded-full" aria-hidden="true" />
          <span>Listening...</span>
        </span>
      ) : null}
      {!listening && error ? (
        <span
          data-testid="voice-error"
          data-voice-error={error}
          className="text-destructive inline-flex max-w-full items-start gap-1.5 text-xs"
        >
          <span className="min-w-0">
            <ErrorMessage error={error} />
          </span>
          <button
            type="button"
            data-testid="voice-error-dismiss"
            onClick={onDismissError}
            className="hover:bg-destructive/10 focus-visible:ring-ring shrink-0 rounded-full p-0.5 focus-visible:ring-2 focus-visible:outline-none"
            aria-label="Close"
          >
            <X className="size-3" />
          </button>
        </span>
      ) : null}
    </div>
  );
}
